import React from "react";
import { ArrowRight, Braces, Check, Copy, X } from "lucide-react";
import { ApiQuery } from "../../types";
import { SyntaxHighlighter } from "~/components/CodeBlock/SyntaxHighlighter";
import { SyntaxHighlighterLanguages } from "~/components/CodeBlock/types";
import {
    getCurlRequest,
    getJavaScriptRequest,
    getPythonRequest,
} from "./snippets";
import {
    trackApiToolsCodeSampleClick,
    trackApiToolsCopyCodeSample,
    trackApiToolsCopyQuery,
} from "./analytics";
import { sanitizeFilter } from "../functions/sanitizeFilter";
import { ApiToolsEvents } from "~/modules/analytics/events/filters";
import { Anchor } from "~/components/Anchor";
import { Link } from "~/components/Link";
import { getApiExplorerRoute } from "./consts";
import { CRMForms } from "~/components/Navbar/CRMForms";
import { RWA_URLS } from "~/consts";
import { cn } from "~/lib/utils";

// // // //
// Building blocks (mirror the "Stories" section of the design spec)

// 9px-wide rounded scrollbar thumb from the spec's .code-scroll rule
const CODE_SCROLL_CLASS_NAME =
    "[&::-webkit-scrollbar]:h-[9px] [&::-webkit-scrollbar]:w-[9px] [&::-webkit-scrollbar-thumb]:rounded-lg [&::-webkit-scrollbar-thumb]:bg-[rgba(120,140,175,0.35)]";

const HEADER_LINK_CLASS_NAME =
    "inline-flex h-8 flex-none items-center gap-[7px] rounded-full border border-[#DAE2F0] bg-white px-[13px] text-xs font-semibold text-[#316CCD] transition-colors hover:border-[#316CCD] hover:bg-[#DFEBFF] hover:text-[#5C97F7]";

/**
 * DrawerHeader
 * Title, API Explorer link, and close button
 */
function DrawerHeader(props: {
    query?: ApiQuery;
    onClose?: () => void;
    apiDocsBaseUrl: string;
    hasApiAccess: boolean;
}) {
    const { hasApiAccess = false } = props;

    // NOTE - users without API access + endpoints without an API Explorer page
    // fall back to the API docs
    const apiExplorerRoute = hasApiAccess
        ? getApiExplorerRoute(props.apiDocsBaseUrl)
        : undefined;

    // Pass the applied query to the API Explorer page via the ?query= parameter
    const apiExplorerHref =
        apiExplorerRoute !== undefined && props.query !== undefined
            ? `${apiExplorerRoute}?query=${encodeURIComponent(JSON.stringify(props.query))}`
            : apiExplorerRoute;

    return (
        <div className="flex flex-none items-center gap-3.5 px-6 pb-1 pt-5">
            <div className="min-w-0 flex-1">
                <div className="text-[11px] font-semibold uppercase tracking-[.08em] text-[#98A7BD]">
                    Api tools
                </div>
                <h2 className="mt-[3px] text-[22px] font-light leading-[1.2] text-[#14171C]">
                    Code samples
                </h2>
            </div>
            {apiExplorerHref !== undefined ? (
                <Link
                    href={apiExplorerHref}
                    openInNewTab
                    analyticsLabel={ApiToolsEvents.ApiExplorerLinkClick}
                    analyticsEventProperties={{
                        apiDocsBaseUrl: props.apiDocsBaseUrl,
                    }}
                    className={HEADER_LINK_CLASS_NAME}
                >
                    Open in API Explorer
                    <ArrowRight size={13} strokeWidth={2} />
                </Link>
            ) : (
                <Anchor
                    href={RWA_URLS.docs}
                    openInNewTab
                    analyticsLabel={ApiToolsEvents.ApiDocsLinkClick}
                    analyticsEventProperties={{
                        apiDocsBaseUrl: props.apiDocsBaseUrl,
                    }}
                    className={HEADER_LINK_CLASS_NAME}
                >
                    API docs
                    <ArrowRight size={13} strokeWidth={2} />
                </Anchor>
            )}
            {props.onClose !== undefined && (
                <button
                    type="button"
                    aria-label="Close"
                    onClick={props.onClose}
                    className="flex h-8 w-8 flex-none cursor-pointer items-center justify-center rounded-lg border border-[#DAE2F0] bg-white text-[#5A6F8F]"
                >
                    <X size={15} strokeWidth={2} />
                </button>
            )}
        </div>
    );
}

/**
 * RequestBar
 * Method chip + endpoint, on dark
 */
function RequestBar(props: { url: string }) {
    return (
        <div className="flex flex-none items-center gap-2 border-b border-[#1E2A44] bg-[#0E1526] px-3.5 py-[11px]">
            <span className="rounded-[5px] bg-[rgba(126,224,176,0.16)] px-1.5 py-1 text-[9px] font-bold leading-none tracking-[.08em] text-[#7EE0B0]">
                GET
            </span>
            <span className="min-w-0 flex-1 truncate font-mono text-xs text-[#C9D4EC]">
                {props.url}
            </span>
        </div>
    );
}

// NOTE - cURL renders as `bash` - the SyntaxHighlighter has no dedicated
// curl language, matching the API Tools "Getting Started" examples
const LANGUAGE_TABS: Array<{
    label: string;
    language: SyntaxHighlighterLanguages;
    getSnippet: (props: { query?: ApiQuery; baseUrl: string }) => string;
}> = [
    {
        label: "Python",
        language: SyntaxHighlighterLanguages.python,
        getSnippet: getPythonRequest,
    },
    {
        label: "JavaScript",
        language: SyntaxHighlighterLanguages.javascript,
        getSnippet: getJavaScriptRequest,
    },
    {
        label: "cURL",
        language: SyntaxHighlighterLanguages.bash,
        getSnippet: getCurlRequest,
    },
];

/**
 * LanguageTabs
 * Underline tabs on dark
 */
function LanguageTabs(props: {
    language: SyntaxHighlighterLanguages;
    onSelect: (language: SyntaxHighlighterLanguages) => void;
}) {
    return (
        <>
            {LANGUAGE_TABS.map((tab) => (
                <button
                    key={tab.label}
                    type="button"
                    onClick={() => props.onSelect(tab.language)}
                    className={cn(
                        "-mb-px h-[42px] cursor-pointer border-0 border-b-2 bg-transparent px-0.5 text-sm font-medium leading-none",
                        props.language === tab.language
                            ? "border-b-[#316CCD] text-white"
                            : "border-b-transparent text-[#7C8BAA]",
                    )}
                >
                    {tab.label}
                </button>
            ))}
        </>
    );
}

/**
 * CopyButton
 * Default and copied states
 */
function CopyButton(props: {
    copied: boolean;
    onClick: () => void;
    label: string;
    icon?: typeof Copy;
}) {
    const Icon = props.icon ?? Copy;

    return (
        <button
            type="button"
            onClick={props.onClick}
            className={cn(
                "inline-flex h-[26px] cursor-pointer items-center gap-1.5 rounded-md border px-2.5 text-[11px] font-medium leading-none",
                props.copied
                    ? "border-[#2A6E52] bg-[rgba(126,224,176,0.12)] text-[#7EE0B0]"
                    : "border-[#2A3A5C] bg-white/[0.04] text-[#B9C6E2]",
            )}
        >
            {props.copied ? (
                <Check size={12} strokeWidth={2} />
            ) : (
                <Icon size={12} strokeWidth={2} />
            )}
            {props.copied ? "Copied!" : props.label}
        </button>
    );
}

/**
 * useCopyFeedback
 * Writes a value to the clipboard and flips the button into its transient
 * "Copied!" state
 */
function useCopyFeedback() {
    const [copied, setCopied] = React.useState(false);
    const copiedTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(
        null,
    );

    React.useEffect(() => {
        return () => {
            if (copiedTimeout.current) {
                clearTimeout(copiedTimeout.current);
            }
        };
    }, []);

    function copy(value: string) {
        try {
            navigator.clipboard?.writeText(value);
        } catch {
            // clipboard unavailable - noop
        }
        setCopied(true);
        if (copiedTimeout.current) {
            clearTimeout(copiedTimeout.current);
        }
        copiedTimeout.current = setTimeout(() => setCopied(false), 1300);
    }

    return { copied, copy };
}

/**
 * CodeWindow
 * Syntax-highlighted, scrollable, line-numbered code on dark
 */
function CodeWindow(props: {
    code: string;
    language: SyntaxHighlighterLanguages;
}) {
    return (
        <div
            className={cn(
                "min-h-0 flex-1 overflow-auto bg-[#0E1526] px-3 py-3.5 font-mono text-[12.5px] leading-[1.7] text-[#C9D4EC]",
                CODE_SCROLL_CLASS_NAME,
            )}
        >
            <SyntaxHighlighter
                code={props.code}
                language={props.language}
                showLineNumbers
                className="whitespace-pre"
            />
        </div>
    );
}

/**
 * AccessCta
 * Soft-light "book a call" card — shown when the user has no API key
 */
function AccessCta() {
    return (
        <div className="flex-none rounded-[13px] border border-[#DFEBFF] bg-[#F4F8FF] p-[18px]">
            <div className="text-[11px] font-semibold uppercase tracking-[.08em] text-[#316CCD]">
                API access required
            </div>
            <h3 className="mt-[5px] text-[17px] font-semibold leading-[1.3] text-[#14171C]">
                Ready to build with RWA.xyz?
            </h3>
            <p className="mt-1.5 text-[13px] leading-[1.55] text-[#324157]">
                <CRMForms
                    buttonText="Talk to our team"
                    buttonClassName="font-semibold text-[#316CCD] underline decoration-[#316CCD]/40 underline-offset-2 transition-colors hover:text-[#5C97F7]"
                    analyticsLabel={ApiToolsEvents.TalkToTeamClick}
                />{" "}
                to unlock trial access to the RWA.xyz API.
            </p>
        </div>
    );
}

// // // //

/**
 * ApiDocs
 * "API Tools" drawer panel — the unified-code-card design from the
 * ApiToolsDrawer Final spec. Accepts the same props as ApiDocs, plus an
 * optional needsApiAccess flag (renders the AccessCta above the code) and an
 * optional onClose handler (renders the close button in the header).
 */
export function ApiDocs(props: {
    query?: ApiQuery;
    baseUrl: string;
    hasApiAccess?: boolean;
    onClose?: () => void;
}) {
    const { hasApiAccess = false, onClose } = props;
    const baseUrl = `https://api.rwa.xyz${props.baseUrl}`;

    const [language, setLanguage] = React.useState<SyntaxHighlighterLanguages>(
        SyntaxHighlighterLanguages.python,
    );
    const codeCopy = useCopyFeedback();
    const queryCopy = useCopyFeedback();

    // NOTE - we manually reconstruct the ApiQuery object here to ensure that ApiQuery.fields is not included
    const query: ApiQuery | undefined = props.query
        ? {
              filter: sanitizeFilter(props.query.filter),
              sort: props.query.sort,
              aggregate: props.query.aggregate,
              pagination: props.query.pagination,
          }
        : undefined;

    // NOTE - we have to manually remove the resultCount and pageCount from the query
    // This is because we pass the QueryResponse.pagination value into the ApiDocs component,
    // which includes the resultCount and pageCount values. We just manually delete them
    if (query?.pagination) {
        // @ts-ignore
        if (query.pagination.resultCount) {
            // @ts-ignore
            delete query.pagination.resultCount;
        }
        // @ts-ignore
        if (query.pagination.pageCount) {
            // @ts-ignore
            delete query.pagination.pageCount;
        }
    }

    const activeTab =
        LANGUAGE_TABS.find((each) => each.language === language) ??
        LANGUAGE_TABS[0];
    const code = activeTab.getSnippet({ query, baseUrl }).trim();

    function onSelectLanguage(nextLanguage: SyntaxHighlighterLanguages) {
        setLanguage(nextLanguage);
        trackApiToolsCodeSampleClick({
            apiDocsBaseUrl: props.baseUrl,
            language: nextLanguage,
        });
    }

    function onCopyCode() {
        codeCopy.copy(code);
        trackApiToolsCopyCodeSample({
            query,
            apiDocsBaseUrl: props.baseUrl,
            language,
        });
    }

    function onCopyQuery() {
        if (query === undefined) {
            return;
        }
        queryCopy.copy(JSON.stringify(query, null, 4));
        trackApiToolsCopyQuery({
            query,
            apiDocsBaseUrl: props.baseUrl,
        });
    }

    return (
        <div className="flex h-full min-h-0 flex-col overflow-hidden bg-white">
            <DrawerHeader
                query={query}
                onClose={onClose}
                hasApiAccess={hasApiAccess}
                apiDocsBaseUrl={props.baseUrl}
            />

            <div
                className={cn(
                    "flex min-h-0 flex-1 flex-col gap-4 overflow-auto px-6 pb-6 pt-0.5",
                    CODE_SCROLL_CLASS_NAME,
                )}
            >
                <p className="flex-none text-[13px] leading-normal text-[#5A6F8F]">
                    Query the RWA.xyz API using the filters applied on your
                    screen.
                </p>

                {!hasApiAccess && <AccessCta />}

                {/* Unified code card */}
                <div className="flex min-h-[300px] flex-1 flex-col overflow-hidden rounded-xl border border-[#DAE2F0]">
                    <RequestBar url={baseUrl} />
                    <div className="flex flex-none gap-[22px] border-b border-[#1E2A44] bg-[#0E1526] px-3.5">
                        <LanguageTabs
                            language={language}
                            onSelect={onSelectLanguage}
                        />
                        <span className="flex-1" />
                        <div className="flex flex-none items-center gap-2 self-center">
                            {query !== undefined && (
                                <CopyButton
                                    label="Copy Query"
                                    icon={Braces}
                                    copied={queryCopy.copied}
                                    onClick={onCopyQuery}
                                />
                            )}
                            <CopyButton
                                label="Copy Code"
                                copied={codeCopy.copied}
                                onClick={onCopyCode}
                            />
                        </div>
                    </div>
                    <CodeWindow code={code} language={language} />
                </div>
            </div>
        </div>
    );
}
